import { ChevronRightOutlineIcon } from '@ikyucom/design-system-icons-react'
import { type MouseEvent, useCallback, useEffect } from 'react'
import Skeleton from 'react-loading-skeleton'
import { CourseSelectionInfo } from '~/components/course/CourseSelectionInfo.frag'
import { NoCourse } from '~/components/course/NoCourse'
import {
  FilterChips,
  useBaseSetApplyPriceRange,
  useClear,
  useIsLoading,
  useSetBuffet,
  useSetChildUse,
  useSetSeatAttributes,
  useSetSmokingAllowed,
} from '~/components/filterChips'
import { PlanSelectionInfo } from '~/components/plan/PlanSelectionInfo.frag'
import { useSeatOnlyCourseNavigation } from '~/features/guide/courses/hooks'
import { NO_COURSE_TEXT, NO_INVENTORY_COURSE_SUB_TEXT } from '~/lib/constants/constants'
import { format } from '~/lib/date/date'
import { SystemError } from '~/lib/error/systemError'
import { getFragmentData } from '~/lib/graphql'
import { useIsPC } from '~/lib/ua'
import { type CourseSelectContext, useReserveNavigate } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { hstack, vstack } from '~/styled-system/patterns'
import { CourseItem as PcCourseItem } from '../../course/pc/CourseComponent'
import { SeatOnlyCourse as PcSeatOnlyCourse } from '../../course/pc/SeatOnlyCourse'
import { CourseItem as SdCourseItem } from '../../course/sd/CourseComponent'
import { SeatOnlyCourse as SdSeatOnlyCourse } from '../../course/sd/SeatOnlyCourse'
import {
  scope,
  useAfternoonTea,
  useBuffet,
  useChildUse,
  useChristmas,
  useCourses,
  useEnabledAfternoonTea,
  useEnabledBuffet,
  useEnabledChildUse,
  useEnabledChristmas,
  useEnabledFreeFlows,
  useEnabledSeatAttributes,
  useEnabledSmokingAllowed,
  useFilterCriteria,
  useFreeFlows,
  useInitialPriceRange,
  useQuery,
  useRestaurant,
  useSeatAttributes,
  useSeatOnlyPlans,
  useSetAfternoonTea,
  useSetChristmas,
  useSetContext,
  useSetFreeFlows,
  useShow,
  useSmokingAllowed,
} from './courseSelection'

export function CourseSelection({
  context,
}: {
  context: CourseSelectContext
}) {
  return (
    <scope.Provider>
      <Body context={context} />
    </scope.Provider>
  )
}

const bodyStyles = sva({
  slots: [
    'list',
    'section',
    'container',
    'heading',
    'name',
    'detail',
    'date',
    'filter',
    'title',
    'coursesHeading',
    'items',
    'note',
  ],
  base: {
    list: vstack.raw({
      gap: '5',
      alignItems: 'start',
      bg: 'gray.100',
      p: '3',
      w: 'full',
      _pc: {
        gap: '6',
        p: '6',
      },
    }),
    section: vstack.raw({
      gap: '3',
      alignItems: 'start',
      w: 'full',
      _pc: {
        gap: '2',
      },
    }),
    container: {
      minHeight: 'full',
      backgroundColor: 'gray.100',
    },
    heading: {
      display: 'flex',
      flexDirection: 'column',
      gap: { base: '2', _pc: '1' },
      px: { base: '3', _pc: '6' },
      py: { base: '3', _pc: '4' },
    },
    name: {
      fontSize: 'lg',
      fontWeight: 'bold',
      _sd: {
        truncate: true,
        w: 'full',
        display: 'inline-block',
      },
      _pc: {
        fontSize: 'xl',
      },
    },
    detail: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 'sm',
      color: 'link',
      flexShrink: '0',
    },
    date: {
      fontSize: 'base',
      _pc: {
        fontSize: 'xl',
      },
    },
    filter: {
      _empty: {
        display: 'none',
      },
      _pc: {
        paddingX: '6',
      },
    },
    title: {
      fontSize: 'lg',
      fontWeight: 'bold',
      _pc: {
        fontSize: '2xl',
      },
    },
    coursesHeading: hstack.raw({
      alignItems: 'center',
      justifyContent: 'space-between',
      w: 'full',
    }),
    items: vstack.raw({
      gap: { base: '2', _pc: '3' },
      alignItems: 'start',
      w: 'full',
    }),
    note: {
      fontSize: { base: 'xs', _pc: 'sm' },
      color: 'gray.600',
    },
  },
})

export function Body({
  context,
}: {
  context: CourseSelectContext
}) {
  const { error, isPending, restaurant, courses, seatOnlyPlans, isLoading } =
    useCourseSelect(context)

  const isPc = useIsPC()
  const SeatOnlyCourse = isPc ? PcSeatOnlyCourse : SdSeatOnlyCourse
  const onClickSeatOnlyCourse = useSeatOnlyCourseNavigation(seatOnlyPlans)
  const CourseItem = isPc ? PcCourseItem : SdCourseItem

  // 「店舗情報」クリックで店舗詳細ふすまに遷移する
  const navigate = useReserveNavigate()
  const onClickDetail = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      e.preventDefault()
      navigate({
        type: 'detail',
      })
    },
    [context, navigate]
  )

  if (error) {
    throw new SystemError(`GetRestaurantInventoryCourses ${error.message ?? ''}`)
  }
  if (isPending) {
    return <CourseSelectFusumaSkeleton context={context} />
  }

  if (restaurant === null || restaurant === undefined) {
    throw new SystemError('GetRestaurantInventoryCourses: restaurant is undefined')
  }

  const classes = bodyStyles()
  return (
    <>
      <div className={classes.container}>
        <div className={classes.heading}>
          <div
            className={css({ display: 'flex', columnGap: '2', justifyContent: 'space-between' })}
          >
            <span className={classes.name}>{restaurant.name}</span>
            {/* TODO: PCでも店舗情報を表示できるようにする */}
            {!isPc && (
              <button type="button" onClick={onClickDetail} className={classes.detail}>
                店舗情報
                <ChevronRightOutlineIcon className={css({ ml: '1' })} />
              </button>
            )}
          </div>
          <span className={classes.date}>
            {format(context.visitDate, 'M月d日(E)')} {context.visitTime}
            {` ${context.visitorsCount}名`}
          </span>
        </div>
        <div className={classes.filter}>
          <FilterChips
            useFilterCriteria={useFilterCriteria}
            useFreeFlows={useFreeFlows}
            useSetFreeFlows={useSetFreeFlows}
            useEnabledFreeFlows={useEnabledFreeFlows}
            useAfternoonTea={useAfternoonTea}
            useEnabledAfternoonTea={useEnabledAfternoonTea}
            useSetAfternoonTea={useSetAfternoonTea}
            useBuffet={useBuffet}
            useSetBuffet={useSetBuffet}
            useEnabledBuffet={useEnabledBuffet}
            useSmokingAllowed={useSmokingAllowed}
            useSetSmokingAllowed={useSetSmokingAllowed}
            useEnabledSmokingAllowed={useEnabledSmokingAllowed}
            useSeatAttributes={useSeatAttributes}
            useSetSeatAttributes={useSetSeatAttributes}
            useEnabledSeatAttributes={useEnabledSeatAttributes}
            useChildUse={useChildUse}
            useSetChildUse={useSetChildUse}
            useEnabledChildUse={useEnabledChildUse}
            useApplyPrice={useBaseSetApplyPriceRange}
            useInitialPriceRange={useInitialPriceRange}
            useChristmas={useChristmas}
            useSetChristmas={useSetChristmas}
            useEnabledChristmas={useEnabledChristmas}
            useClear={useClear}
            useShow={useShow}
          />
        </div>
        <div className={classes.list}>
          {courses.length > 0 || seatOnlyPlans.length > 0 ? (
            <>
              {seatOnlyPlans.length > 0 && (
                <div className={classes.section}>
                  <SeatOnlyCourse
                    onClick={onClickSeatOnlyCourse}
                    onlyRequestReservations={seatOnlyPlans.every(
                      (plan) => plan.isRequestReservation
                    )}
                    fixedPoints={Math.max(
                      ...seatOnlyPlans.map((item) => {
                        return item.fixedPoints
                      })
                    )}
                    pageType="courseSelectFusuma"
                  />
                </div>
              )}
              {courses.length > 0 && (
                <div className={classes.section}>
                  <div className={classes.coursesHeading}>
                    <span className={classes.title}>コースを選んで予約</span>
                    <div className={classes.note}>お一人様 消費税・サービス料込</div>
                  </div>
                  {isLoading ? (
                    <Loading />
                  ) : (
                    <div className={classes.items}>
                      {courses.map((item, index) => {
                        const course = getFragmentData(CourseSelectionInfo, item.course)
                        const plans = item.plans.map((p) => ({
                          ...getFragmentData(PlanSelectionInfo, p.plan),
                          bestPrice: p.price,
                          priceVaried: false,
                          discountRate: p.discountRatePercent ?? 0,
                          pointRate: p.pointRatePercent,
                          fixedPoints: p.fixedPoints,
                        }))
                        return (
                          <CourseItem
                            key={index}
                            course={course}
                            plans={plans}
                            restaurant={restaurant}
                            pageType="courseSelectFusuma"
                          />
                        )
                      })}
                      <div className={classes.note}>
                        <p>
                          ※表示されている料金は最新の状況と異なる場合があります。予約情報入力画面にて合計金額をご確認ください。
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <NoCourse
              restaurantId={context.restaurantId}
              mainText={NO_COURSE_TEXT.main}
              subText={NO_INVENTORY_COURSE_SUB_TEXT}
              buttonLabel="店舗トップへ戻る"
            />
          )}
        </div>
      </div>
    </>
  )

  function useCourseSelect(context: CourseSelectContext) {
    const set = useSetContext()
    const { isPending, error } = useQuery()
    const courses = useCourses()
    const seatOnlyPlans = useSeatOnlyPlans()
    const restaurant = useRestaurant()
    const isLoading = useIsLoading('COURSE_SELECT')
    useEffect(() => {
      set(context)
    }, [context, set])
    return { error, isPending, restaurant, courses, seatOnlyPlans, isLoading }
  }
}

function CourseSelectFusumaSkeleton({
  context,
}: {
  context: CourseSelectContext
}) {
  return (
    <>
      <div className={css({ minHeight: 'full', backgroundColor: 'gray.100' })}>
        <div className={vstack({ gap: '2', p: '3', alignItems: 'start' })}>
          <div
            className={css({
              width: 'full',
              display: 'flex',
              columnGap: '2',
              justifyContent: 'space-between',
            })}
          >
            <Skeleton width={195} height={14} />
            <Skeleton width={64} height={12} />
          </div>
          <Skeleton width={139} height={13} />
        </div>
        <div className={css({ display: 'flex', px: '3', pb: '2' })}>
          <Skeleton width={283} height={32} />
        </div>
        <div className={css({ spaceY: '3', px: '3', py: '3' })}>
          <SeatOnlyCardSkeleton />
        </div>
        <div className={css({ spaceY: '3', px: '3', py: '3' })}>
          <Skeleton width={132} height={15} />
          <CourseCardSkeleton />
          <CourseCardSkeleton />
          <CourseCardSkeleton />
        </div>
      </div>
    </>
  )
}

// 席のみカードのスケルトン
function SeatOnlyCardSkeleton() {
  return (
    <div
      className={css({
        width: 'full',
        display: 'flex',
        columnGap: '3',
        rounded: 'md',
        border: 'light',
        p: '3',
        bg: 'white',
      })}
    >
      <div className={vstack({ alignItems: 'start', width: 'full', gap: '1' })}>
        <Skeleton height={15} width={60} />
        <Skeleton height={12} width={108} />
      </div>
    </div>
  )
}

// コースカードのスケルトン
function CourseCardSkeleton() {
  return (
    <div
      className={css({
        width: 'full',
        display: 'flex',
        columnGap: '3',
        rounded: 'md',
        border: 'light',
        p: '3',
        bg: 'white',
      })}
    >
      <Skeleton height={68} width={96} />
      <div className={vstack({ alignItems: 'start', width: 'full', gap: '4' })}>
        <div className={css({ spaceY: '2' })}>
          <Skeleton height={13} width={219} />
          <Skeleton height={13} width={120} />
        </div>
        <div
          className={css({
            width: 'full',
            display: 'flex',
            justifyContent: 'space-between',
          })}
        >
          <Skeleton height={13} width={84} />
          <Skeleton height={13} width={54} />
        </div>
      </div>
    </div>
  )
}

function Loading() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-1 items-start justify-start space-x-3 rounded-md border border-neutral-200 bg-neutral-000 p-3"
        >
          <Skeleton height={68} width={96} />
          <div className="flex flex-col ">
            <Skeleton height={13} width={219} />
            <Skeleton height={13} width={120} />
            <div className="mt-4 flex justify-between">
              <Skeleton height={13} width={84} />
              <Skeleton height={13} width={54} />
            </div>
          </div>
        </div>
      ))}
    </>
  )
}
